import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { ElementRef, Injectable, Injector } from '@angular/core';

import { awaitHandler } from '@utils/awaitHandler';
import { IObjectStringKeyMap } from '@shared/models';
import { FormFileUploaderComponent, FormTranslatedFileUploaderComponent } from '@shared/components';
import { FormTranslatedFileUploaderConfig } from '@shared/components/form-translated-file-uploader/model';
import { BaseDetailsPage } from '@modules/management/pages/details/abstractDetails/abstractBaseDetails.page';
import { FormFileUploaderConfig, FormFileUploaderMetadata } from '@shared/components/form-file-uploader/model';
import {
  AccountsService,
  FormBuilderService,
  FormField,
  LoadingService,
  ObjectItem,
  ObjectsService,
  PopoverService,
  SettingsService,
  UserdataService,
  UtilsService
} from '@services';

import { TranslateService } from '@ngx-translate/core';
import { each, find, get, identity, isEmpty, pickBy, sortBy } from 'lodash';

export enum ContentBaseField {
  Description = 'description',
  ContentObject = 'contentObject',
  TranslatedContentObject = 'translatedContentObject',
  Revision = 'revision',
  Tags = 'tags',
  Resources = 'users'
}

@Injectable()
export abstract class AbstractContentBasePage extends BaseDetailsPage {
  public objectData: ObjectItem;

  protected accountsService: AccountsService = this.injector.get(AccountsService);
  protected utils: UtilsService = this.injector.get(UtilsService);
  protected objectsService: ObjectsService = this.injector.get(ObjectsService);
  protected settingsService: SettingsService = this.injector.get(SettingsService);
  protected userdataService: UserdataService = this.injector.get(UserdataService);
  protected sharedFields = {
    [ContentBaseField.Description]: {
      inputtype: 'verbatim',
      required: true,
      name: 'description',
      title: this.translate.instant('MGMT_DETAILS.Title'),
      type: 'text',
      size: 20
    },
    [ContentBaseField.ContentObject]: {
      containerClass: 'custom-form-element',
      type: 'customElement',
      required: true,
      title: 'CONTENT.Module_Name',
      name: 'objectID',
      component: FormFileUploaderComponent,
      inputs: {
        config: <FormFileUploaderConfig>{
          deferredUpload: true,
          accept: '*',
          metadata: {
            type: 'content',
            subtype: null
          }
        }
      },
      hideFieldsOnValue: {
        select: [
          {
            hideClasses: ['translation-content-objects'],
            valueFunction: (val) => !val
          }
        ]
      }
    },
    [ContentBaseField.TranslatedContentObject]: {
      containerClass: 'translation-content-objects',
      type: 'customElement',
      name: 'translatedObjectID',
      component: FormTranslatedFileUploaderComponent,
      inputs: {
        config: {
          deferredUpload: true,
          accept: 'image/*, audio/*, video/*, .pdf, .doc, .docx, .xml',
          metadata: {
            type: 'content',
            subtype: null
          },
          title: 'MGMT_DETAILS.Content_Translation'
        } as FormTranslatedFileUploaderConfig
      }
    },
    [ContentBaseField.Revision]: {
      required: true,
      inputtype: 'verbatim',
      name: 'revision',
      title: this.translate.instant('CONTENT.Revision'),
      type: 'text',
      size: 20
    },
    [ContentBaseField.Tags]: {
      title: this.translate.instant('SHARED.Tags'),
      name: 'tags',
      type: 'selectmenu',
      multiple: true,
      tags: true,
      options: sortBy(this.settingsService.customTags.data, 'tag'),
      canClear: false,
      valueProperty: 'tagID',
      func: (item: any) => item.tag,
      placeholder: this.translate.instant('SHARED.Add_Tags')
    },
    [ContentBaseField.Resources]: {
        title: this.translate.instant('MGMT_DETAILS.Go_Tos'),
        type: 'selectmenu',
        name: 'users',
        multiple: true,
        valueProperty: 'userID',
        placeholder: '',
        options: this.accountsService.getUserlist(this.userdataService.locations),
        test(ref) {
          if (ref?.disabledAt || !ref?.active) {
            return false;
          } else {
            if (ref.hasOwnProperty('type') && (ref.type === 'dedicated' || ref.type === 'shared')) {
              return true;
            } else {
              return false;
            }
          }
        },
        func: (ref) => this.accountsService.fullUserName(ref.userID)
      },
  };

  protected constructor(protected injector: Injector) {
    super(
      injector.get(ActivatedRoute),
      injector.get(FormBuilderService),
      injector.get(ElementRef),
      injector.get(PopoverService),
      injector.get(Location),
      injector.get(LoadingService),
      injector.get(TranslateService),
    );
  }

  protected async prepareDataAsync(): Promise<any> {
    await this.loadingService.enable();
    const customTagsHandler: Promise<any> = isEmpty(this.settingsService.customTags.data) ?
      this.settingsService.getCustomTags() : Promise.resolve();
    await awaitHandler(Promise.all([customTagsHandler]));
    this.loadingService.disable();

    const tagField: FormField = find(this.formConfig.fields, {name: 'tags'});
    if (tagField) {
      tagField.options = sortBy(this.settingsService.customTags.data, 'tag');
    }
    const resourcesField: FormField = find(this.formConfig.fields, {name: 'users'});
    if (resourcesField) {
      resourcesField.options = this.accountsService.accounts.data;
    }
  }

  protected decodeReviewFields(objectItem: ObjectItem) {
    each(objectItem?.attributes?.reviewInfo, (value, name) => {
      objectItem[name] = isNaN(+value) ? value : +value;
    });
  }

  protected decodeTranslationsObjects() {
    each(get(this.objectData, 'translations.objects'), (objectTranslation: IObjectStringKeyMap<any>) => {
      if (objectTranslation.language) {
        this.objectData[`translation_contentObject_${objectTranslation.language}`] = objectTranslation.value;
      }
    });
  }

  protected savePendingObjects(metadata?: FormFileUploaderMetadata): Promise<ObjectItem[]> {
    metadata = pickBy(metadata, identity);
    const mediaField = find(this.formConfig.fields, { component: FormFileUploaderComponent });
    return (mediaField?.componentRef as FormFileUploaderComponent)?.upload(metadata) || Promise.resolve([]);
  }

  protected saveTranslationPendingObjects(metadata?: FormFileUploaderMetadata): Promise<ObjectItem[]> {
    metadata = pickBy(metadata, identity);
    const mediaField = find(this.formConfig.fields, { component: FormTranslatedFileUploaderComponent });
    return (mediaField.componentRef as FormTranslatedFileUploaderComponent)?.upload(metadata) || Promise.resolve([]);
  }
}
